import { createClient, SupabaseClient } from '@supabase/supabase-js';
import { SessionMonitor } from '@/utils/security';
import { Database } from '@/types/database';

// Validate environment variables
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

if (!supabaseUrl || !supabaseAnonKey) {
  throw new Error(
    'Missing required environment variables: VITE_SUPABASE_URL and VITE_SUPABASE_ANON_KEY'
  );
}

// URL validation
try {
  new URL(supabaseUrl);
} catch {
  throw new Error('Invalid Supabase URL');
}

// Create a singleton Supabase client
let supabaseInstance: SupabaseClient<Database> | null = null;

export const supabase = (() => {
  if (supabaseInstance) return supabaseInstance;
  
  supabaseInstance = createClient<Database>(supabaseUrl, supabaseAnonKey, {
    auth: {
      persistSession: true,
      storageKey: 'educhat-auth',
      storage: window.localStorage,
      detectSessionInUrl: true,
      autoRefreshToken: true,
    },
    global: {
      headers: {
        'x-client-info': 'educhat@1.0.0',
      },
    },
  });
  
  return supabaseInstance;
})();

// Session monitor instance
let sessionMonitor: SessionMonitor | null = null;

// Session management helpers
export const initializeSessionMonitor = (
  onExpiry: () => void,
  onWarning?: () => void
) => {
  sessionMonitor = new SessionMonitor(onExpiry, onWarning);

  // Subscribe to auth state changes
  supabase.auth.onAuthStateChange((event, session) => {
    if (event === 'SIGNED_IN' && session) {
      const expiresAt = session.expires_at
        ? new Date(session.expires_at * 1000)
        : null;
      if (expiresAt) {
        sessionMonitor?.start(expiresAt);
      }
    } else if (event === 'SIGNED_OUT') {
      sessionMonitor?.stop();
    } else if (event === 'TOKEN_REFRESHED' && session) {
      const expiresAt = session.expires_at
        ? new Date(session.expires_at * 1000)
        : null;
      if (expiresAt) {
        sessionMonitor?.reset(expiresAt);
      }
    }
  });
};

// Clean up session monitor
export const cleanupSessionMonitor = () => {
  sessionMonitor?.stop();
  sessionMonitor = null;
};

// Helper to get the current user with proper error handling
export const getCurrentUser = async () => {
  try {
    const {
      data: { user },
      error,
    } = await supabase.auth.getUser();
    if (error) throw error;
    return user;
  } catch (error) {
    console.error('Error getting current user:', error);
    return null;
  }
};

// Helper to get the current session
export const getCurrentSession = async () => {
  try {
    const {
      data: { session },
      error,
    } = await supabase.auth.getSession();
    if (error) throw error;
    return session;
  } catch (error) {
    console.error('Error getting current session:', error);
    return null;
  }
};

// Sign out helper with cleanup
export const signOut = async () => {
  try {
    cleanupSessionMonitor();
    const { error } = await supabase.auth.signOut();
    if (error) throw error;
  } catch (error) {
    console.error('Error signing out:', error);
    throw error;
  }
};
